// v77.99.20 — Snapshot export date-range presets.
//
// Single source of truth for the "Expenses Snapshot" range picker.
// Each preset returns the resolved {from, to} dates (YYYY-MM-DD) plus
// the human label the PDF should show ("Year to date", "Last 12
// months", etc).
//
// HOW TO ADD A NEW PRESET (no backend change required):
//
//   1. Append an entry to SNAPSHOT_PRESETS below with an `id`, the
//      visible `label`, and a `compute(now)` function that returns
//      `{ from: "YYYY-MM-DD", to: "YYYY-MM-DD" }`.
//   2. The menu component reads this list directly — your new option
//      shows up in the dropdown automatically.
//   3. The backend already accepts arbitrary `from`/`to`/`range_label`
//      query params, so no API change is needed.
//
// Already-planned future presets (commented out — uncomment when
// scoping is finalised):
//   • "school-year"   → 1 Sept (last) → 31 Aug (next) — IE / UK term
//   • "fy-ie"         → 1 Jan → 31 Dec (Ireland personal tax year)
//   • "fy-uk"         → 6 Apr → 5 Apr (UK personal tax year)
//   • "per-child-annual" → wired via the child-scope dropdown; the
//     preset itself stays generic and reuses "last-12-months".
//
// Date math is intentionally local-time to match what the user types
// into a date input. The backend treats both ends inclusively.

/** Format a Date as YYYY-MM-DD using local clock values. */
function toIso(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** First day of the month containing `d`. */
function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

/** Last day of the month containing `d`. */
function endOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

export const SNAPSHOT_PRESETS = [
  {
    id: "current-month",
    label: "Current month",
    description: "1st of this month to today",
    compute: (now = new Date()) => ({
      from: toIso(startOfMonth(now)),
      to: toIso(now),
    }),
  },
  {
    id: "previous-month",
    label: "Previous month",
    description: "The whole of last month",
    compute: (now = new Date()) => {
      const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      return {
        from: toIso(startOfMonth(prev)),
        to: toIso(endOfMonth(prev)),
      };
    },
  },
  {
    id: "year-to-date",
    label: "Year to date",
    description: "1 Jan this year to today",
    compute: (now = new Date()) => ({
      from: toIso(new Date(now.getFullYear(), 0, 1)),
      to: toIso(now),
    }),
  },
  {
    // v77.99.21 — School year preset (1 Sept → 30 June).
    //
    // Auto-detects the current academic year based on today's date:
    //   • Jan–Jun  → school year that STARTED last Sept ended this Jun.
    //     (e.g. Mar 2026 → 1 Sep 2025 – 30 Jun 2026)
    //   • Jul–Aug  → between school years; report the one just ended.
    //     (e.g. Aug 2026 → 1 Sep 2025 – 30 Jun 2026)
    //   • Sep–Dec  → currently inside a school year that ends next Jun.
    //     (e.g. Oct 2026 → 1 Sep 2026 – 30 Jun 2027)
    // Driven by separated parents' real workflow: uniforms, books,
    // school transport, after-school clubs etc. typically reconcile
    // against the academic period, not the calendar year.
    id: "school-year",
    label: "School year",
    description: "1 Sept – 30 June (current academic year)",
    compute: (now = new Date()) => {
      const month = now.getMonth(); // 0=Jan
      const year = now.getFullYear();
      let startYear;
      if (month >= 8) {
        // Sept onwards — academic year starts this calendar year
        startYear = year;
      } else {
        // Jan–Aug — academic year started last September
        startYear = year - 1;
      }
      return {
        from: `${startYear}-09-01`,
        to: `${startYear + 1}-06-30`,
      };
    },
  },
  {
    id: "last-12-months",
    label: "Last 12 months",
    description: "Rolling 12 months ending today",
    compute: (now = new Date()) => {
      const start = new Date(now);
      start.setFullYear(start.getFullYear() - 1);
      start.setDate(start.getDate() + 1); // inclusive 12-month span
      return { from: toIso(start), to: toIso(now) };
    },
  },
  {
    id: "custom",
    label: "Custom date range…",
    description: "Pick your own start and end",
    // No `compute` — the custom preset opens a dialog and supplies
    // {from, to} directly from user input.
    compute: null,
  },
];

/** Find a preset by id. Returns undefined when not found. */
export function findPreset(id) {
  return SNAPSHOT_PRESETS.find((p) => p.id === id);
}

/** Resolve a preset id to {from, to, label}. Returns null for "custom"
 *  (the caller must collect dates from the dialog and synthesise the
 *  result themselves). */
export function resolvePreset(id, now = new Date()) {
  const p = findPreset(id);
  if (!p || !p.compute) return null;
  const { from, to } = p.compute(now);
  return { from, to, label: p.label };
}
